import {
    ActivityEventAssignmentType,
    ActivityEventCategoryFlag,
    ActivityEventFieldType,
    IActivityEventAssignmentChild,
    IActivityEventFieldEntryChild,
    IAlertResponse,
    IAnalyticsModelResponse,
    IHierarchyChild,
    IIndicatorResponse,
    ITreeItemResponse,
    IUserChild,
} from '@kcftech/prometheus-client';
import { ActivityEventBinds } from 'custom_hooks/data_hooks/activity/useActivityEventBinds';
import { indicatorOrderBy } from 'custom_hooks/data_hooks/indicator/useIndicatorCollection';
import { ReactNode } from 'react';
import {
    excludeNullAndUndefined,
    groupBy,
    lastItem,
    skipLast,
    sortBy,
    takeWhile,
    unique,
    uniqueBy,
} from 'utils/array';
import { buildTooltip } from 'utils/group_hierarchy';
import { isValidNumber } from 'utils/number';
import { Lookup } from 'utils/types';

export const savingFieldCode = 'savings';

export type SupportedActivityEventCategoryFlag = Exclude<
    ActivityEventCategoryFlag,
    ActivityEventCategoryFlag.None
>;

export const supportedCategoryList: SupportedActivityEventCategoryFlag[] = [
    ActivityEventCategoryFlag.CriticalAlarm,
    ActivityEventCategoryFlag.Alarm,
    ActivityEventCategoryFlag.Warning,
    ActivityEventCategoryFlag.Automatic,
    ActivityEventCategoryFlag.Manual,
    ActivityEventCategoryFlag.MachineIQ,
];

export type ActivityEventDetail = Readonly<{
    id: string;
    label: string;
    value: ReactNode;
}>;

export type NameAndTooltip = Readonly<{
    name: string;
    tooltip: string;
}>;

type NameAndHierarchy = Readonly<{
    Name: string;
    Hierarchy?: IHierarchyChild[] | undefined;
}>;

const getNamesAndTooltips = (entities: NameAndHierarchy[]): NameAndTooltip[] =>
    entities.map(({ Name, Hierarchy }) => ({
        name: buildTooltip(Hierarchy ?? [], undefined, Name, false),
        tooltip: buildTooltip(Hierarchy ?? [], undefined, Name, true),
    }));

/**
 * Returns the Asset detail from the supplied hierarchy.
 */
export const findAssetDetailInHierarchy = (
    hierarchy?: IHierarchyChild[],
): NameAndTooltip | undefined => {
    if (!hierarchy || hierarchy.length === 0) {
        return undefined;
    }
    const assetHierarchy = takeWhile(
        hierarchy,
        ({ HierarchyType }) => HierarchyType !== 'Asset',
        true,
    );
    const asset = lastItem(assetHierarchy);

    return asset && asset.HierarchyType === 'Asset'
        ? {
              name: buildTooltip(
                  skipLast(assetHierarchy, 1),
                  undefined,
                  asset.HierarchyName,
                  false,
              ),
              tooltip: buildTooltip(
                  skipLast(assetHierarchy, 1),
                  undefined,
                  asset.HierarchyName,
                  true,
              ),
          }
        : undefined;
};

/**
 * Returns the unique "assigned to" details from bound groups and indicators.
 * The Asset from any bound indicators is returned.
 */
export const getAssignedToDetail = (
    groups: ITreeItemResponse[] | undefined = [],
    indicators: Pick<IIndicatorResponse, 'Hierarchy'>[] | undefined = [],
): NameAndTooltip[] | undefined => {
    const details = excludeNullAndUndefined([
        ...getNamesAndTooltips(groups),
        ...indicators.map(({ Hierarchy }) => findAssetDetailInHierarchy(Hierarchy)),
    ]);

    return details.length > 0
        ? sortBy(
              uniqueBy(details, (detail) => detail.tooltip),
              (detail) => detail.name,
          )
        : undefined;
};

/**
 * Returns a formatted list of indicators.
 */
export const getIndicatorDetail = (
    indicators: IIndicatorResponse[] | undefined,
): NameAndTooltip[] | undefined =>
    indicators && indicators.length > 0
        ? getNamesAndTooltips(sortBy(indicators, ...indicatorOrderBy))
        : undefined;

/**
 * Returns a formatted list of models.
 */
export const getModelDetail = (models: IAnalyticsModelResponse[] | undefined) =>
    models && models.length > 0 ? models.map(({ Name }) => Name).join(', ') : undefined;

/**
 * Returns the formatted label and value for a field.
 *
 * @param param0 An activity field child.
 */
export const getFieldDetail = ({
    FieldEntryId,
    FieldName,
    FieldType,
    FieldValue,
    FieldCode,
}: IActivityEventFieldEntryChild): ActivityEventDetail | undefined => {
    switch (FieldType) {
        case ActivityEventFieldType.Bool:
            return {
                id: `field-${FieldEntryId}`,
                label: FieldName ?? '',
                value: FieldValue === 'true' ? 'Yes' : 'No',
            };

        case ActivityEventFieldType.String:
        case ActivityEventFieldType.Int:
        case ActivityEventFieldType.Float:
            return {
                id: `field-${FieldEntryId}`,
                label: FieldName ?? '',
                value:
                    // TODO: it would be better to have an ActivityEventFieldType.Currency or something so
                    // that we wouldn't have to have this type of specific handling.
                    FieldCode === savingFieldCode
                        ? getFormattedSavingsValue(FieldValue)
                        : (FieldValue ?? ''),
            };

        default:
            return undefined;
    }
};

const getFormattedSavingsValue = (savings: string | undefined) => {
    if (!savings || !isValidNumber(savings)) {
        return '';
    }
    return `$${formatSavings(savings)}`;
};

export const formatSavings = (savings: string) => {
    const numberArr = savings.match(/^(\d+)((?:\.\d*)?)$/);
    return numberArr !== null
        ? numberArr[1].replace(/(\d)(?=(?:\d{3})+$)/g, '$1,') + numberArr[2]
        : savings;
};

/**
 * Returns details for field.
 *
 * @param fields List of activity fields.
 */
export const getFieldDetails = (
    fields: IActivityEventFieldEntryChild[],
): ActivityEventDetail[] =>
    sortBy(
        excludeNullAndUndefined(
            fields.filter(({ FieldType }) => !!FieldType).map(getFieldDetail),
        ),
        ({ label }) => label,
    );

export type GetAllDetailsOptions = Readonly<{
    bindData: ActivityEventBinds | undefined;
    fields: IActivityEventFieldEntryChild[] | undefined;
}>;

const assignmentTypeDescriptions: Lookup<ActivityEventAssignmentType, string> = {
    [ActivityEventAssignmentType.Created]: 'Created',
    [ActivityEventAssignmentType.Updated]: 'Last modified',
    [ActivityEventAssignmentType.AssignedTo]: 'Unsupported',
    [ActivityEventAssignmentType.AssignedBy]: 'Unsupported',
};

const assignmentTypeSecondarySort: Lookup<ActivityEventAssignmentType, number> = {
    [ActivityEventAssignmentType.Created]: 1,
    [ActivityEventAssignmentType.Updated]: 2,
    [ActivityEventAssignmentType.AssignedBy]: 3,
    [ActivityEventAssignmentType.AssignedTo]: 4,
};

export type ActivityEventHistory = Readonly<{
    id: string;
    who: string;
    email?: string;
    what: string;
    when: Date;
}>;

export const getUserDisplay = (user?: IUserChild) =>
    user ? `${user.UserFirstName} ${user.UserLastName}` : 'Unknown';

/**
 * Transforms an assignment to activity event history.
 */
export const assignmentToHistory = ({
    AssignmentId,
    AssignmentDateTime,
    AssignmentType,
    AssignmentUser,
}: IActivityEventAssignmentChild): ActivityEventHistory => ({
    id: `assignment-${AssignmentId}`,
    who: getUserDisplay(AssignmentUser),
    email: AssignmentUser?.UserEmail,
    what: assignmentTypeDescriptions[AssignmentType],
    when: AssignmentDateTime,
});

/**
 * Transforms an alert to activity event history.
 */
export const alertToHistory = (
    alert: IAlertResponse | undefined,
): ActivityEventHistory | undefined =>
    alert && alert.TimeAcknowledged
        ? {
              id: `alert-${alert.Id}`,
              who: getUserDisplay(alert.AcknowledgeByUser),
              email: alert.AcknowledgeByUser?.UserEmail,
              what: 'Acknowledged',
              when: alert.TimeAcknowledged,
          }
        : undefined;

/**
 * Returns the most recent assignment history for each type of assignment.
 *
 * @param assignments List of assignments.
 */
export const getAssignmentHistory = (assignments: IActivityEventAssignmentChild[]) => {
    const sortedAssignments = sortBy(
        assignments,
        ({ AssignmentDateTime }) => AssignmentDateTime,
        ({ AssignmentType }) => assignmentTypeSecondarySort[AssignmentType],
    );
    const types = unique(sortedAssignments.map(({ AssignmentType }) => AssignmentType));
    const groups = groupBy(sortedAssignments, ({ AssignmentType }) => AssignmentType);

    return types.map((type) => {
        const group = groups[type];
        if (!group || !group[group.length - 1]) {
            throw new Error('Expected assignments');
        }
        return assignmentToHistory(group[group.length - 1]);
    });
};

/**
 * Builds Activity Event history from assignment and alert data.
 *
 * @param assignments List of assignments.
 * @param alert Optional bound alert.
 */
export const getActivityEventHistory = (
    assignments: IActivityEventAssignmentChild[],
    alert?: IAlertResponse,
) =>
    sortBy(
        excludeNullAndUndefined([
            ...getAssignmentHistory(assignments),
            alertToHistory(alert),
        ]),
        ({ when }) => when,
    );
